import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Avatar, Button, Card, Empty, Skeleton, Space, Tag, Typography } from 'antd';
import { MessageOutlined, RightOutlined } from '@ant-design/icons';
import type { GuardianWard } from '@velora/contracts';
import { productApi } from '../../api';

/**
 * Guardian — Home. Ward picker. Each ward card jumps into their per-ward view.
 */
export function GuardianHomePage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['guardian-home', institution],
    queryFn: () => productApi.getGuardianHome(institution),
    enabled: !!institution,
  });

  if (q.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!q.data) return <Alert type="error" showIcon message="Could not load home" />;

  const { guardian, wards } = q.data;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {!guardian && (
        <Alert
          type="warning"
          showIcon
          message="Your login email isn't linked to a guardian record"
          description="Ask the school to update the guardian email on your ward's record so this page can show their details."
        />
      )}
      {guardian && (
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Hi {guardian.name.split(' ')[0]}</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            {wards.length} ward{wards.length === 1 ? '' : 's'} on file
          </Typography.Text>
        </div>
      )}

      {guardian && (
        <Button
          block
          icon={<MessageOutlined />}
          onClick={() => navigate(`/i/${institution}/parent/messages`)}
        >
          School messages
        </Button>
      )}

      {guardian && wards.length === 0 && (
        <Empty description="No wards linked yet." style={{ padding: 20 }} />
      )}

      {wards.map((w) => (
        <WardCard key={w.student_id} ward={w} onOpen={() => navigate(`/i/${institution}/parent/${w.admission_number}`)} />
      ))}
    </Space>
  );
}

function WardCard({ ward, onOpen }: { ward: GuardianWard; onOpen: () => void }) {
  return (
    <Card size="small" hoverable onClick={onOpen}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Avatar size={44} style={{ background: `hsl(${(ward.name.length * 37) % 360}, 40%, 60%)`, fontSize: 15 }}>
          {ward.name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
        </Avatar>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{ward.name}</div>
          <div style={{ color: '#5b6478', fontSize: 12.5 }}>
            {ward.grade}{ward.section ? `-${ward.section}` : ''} · {ward.gender}
            {' · '}
            <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{ward.admission_number}</span>
          </div>
        </div>
        <Space size={4}>
          {ward.is_primary && <Tag color="success">primary</Tag>}
          {ward.status !== 'enrolled' && <Tag>{ward.status}</Tag>}
          <RightOutlined style={{ color: '#8a94a6' }} />
        </Space>
      </div>
    </Card>
  );
}
